import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommentThread, ResponseDetail } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../../lib/api.js';
import { useAuth, useOrg } from '../../../lib/auth.js';
import { Badge, Button, EmptyState, Spinner, Textarea, formatDateTime, useToast } from '../../../components/ui.js';
import { Markdown } from '../../../components/Markdown.js';

const KIND_TONES = { reviewer: 'warning', assessor: 'purple', reply: 'neutral' } as const;

export function CommentsPanel({ response }: { response: ResponseDetail }) {
  const { org } = useOrg();
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const locked = response.status === 'locked';
  const role = org.role;

  const comments = useQuery({
    queryKey: ['comments', response.id],
    queryFn: () => apiFetch<CommentThread[]>(`/responses/${response.id}/comments`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['comments', response.id] });
    void queryClient.invalidateQueries({ queryKey: ['response', response.id] });
    void queryClient.invalidateQueries({ queryKey: ['responses', response.assessmentId] });
  };
  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Comment action failed', 'error');

  const [newComment, setNewComment] = useState('');
  const create = useMutation({
    mutationFn: () =>
      apiFetch(`/responses/${response.id}/comments`, {
        method: 'POST',
        body: { body: newComment.trim() },
      }),
    onSuccess: () => {
      setNewComment('');
      invalidate();
    },
    onError,
  });

  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const reply = useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/comments/${commentId}/replies`, { method: 'POST', body: { body: replyBody.trim() } }),
    onSuccess: () => {
      setReplyBody('');
      setReplyTo(null);
      invalidate();
    },
    onError,
  });

  const resolve = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'resolved' | 'rejected' | 'open' }) =>
      apiFetch(`/comments/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: invalidate,
    onError,
  });

  const canComment =
    !locked &&
    (role === 'admin' ||
      role === 'reviewer' ||
      (role === 'assessor' && ['finalised', 'assessed'].includes(response.status)));
  const canResolve = (thread: CommentThread) =>
    !locked &&
    (role === 'admin' ||
      thread.author.id === me?.user.id ||
      (role === 'reviewer' && thread.kind === 'reviewer') ||
      (role === 'assessor' && thread.kind === 'assessor'));

  if (comments.isLoading) return <Spinner label="Loading comments" />;

  return (
    <div>
      {(comments.data ?? []).length === 0 && (
        <EmptyState title="No comments yet" message="Reviewer and assessor feedback appears here." />
      )}

      {(comments.data ?? []).map((thread) => (
        <div key={thread.id} className={`comment-item ${thread.status !== 'open' ? 'resolved' : ''}`}>
          <div className="comment-head">
            <span className="author">{thread.author.name}</span>
            <Badge tone={KIND_TONES[thread.kind]}>{thread.kind}</Badge>
            <span>{formatDateTime(thread.createdAt)}</span>
            {thread.status !== 'open' && (
              <Badge tone={thread.status === 'resolved' ? 'success' : 'neutral'}>{thread.status}</Badge>
            )}
          </div>
          <Markdown markdown={thread.body} />
          {thread.resolutionNote && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              Resolution: {thread.resolutionNote}
            </p>
          )}

          {thread.replies.map((r) => (
            <div key={r.id} className="comment-reply">
              <div className="comment-head">
                <span className="author">{r.author.name}</span>
                <span>{formatDateTime(r.createdAt)}</span>
              </div>
              <Markdown markdown={r.body} />
            </div>
          ))}

          {!locked && (
            <div className="comment-actions">
              {replyTo === thread.id ? null : (
                <Button size="sm" variant="ghost" onClick={() => setReplyTo(thread.id)}>
                  Reply
                </Button>
              )}
              {thread.status === 'open' && canResolve(thread) && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => resolve.mutate({ id: thread.id, status: 'resolved' })}
                  >
                    Resolve
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => resolve.mutate({ id: thread.id, status: 'rejected' })}
                  >
                    Reject
                  </Button>
                </>
              )}
              {thread.status !== 'open' && canResolve(thread) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => resolve.mutate({ id: thread.id, status: 'open' })}
                >
                  Reopen
                </Button>
              )}
            </div>
          )}

          {replyTo === thread.id && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (replyBody.trim()) reply.mutate(thread.id);
              }}
              style={{ marginTop: 8 }}
            >
              <Textarea
                aria-label="Reply"
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                style={{ minHeight: 64 }}
              />
              <div className="comment-actions">
                <Button size="sm" type="submit" loading={reply.isPending}>
                  Send reply
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      ))}

      {locked && (
        <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
          This response is locked — comments are read-only.
        </p>
      )}

      {canComment && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newComment.trim()) create.mutate();
          }}
        >
          <Textarea
            aria-label="New comment"
            placeholder={
              role === 'assessor' ? 'Add an assessor comment…' : 'Add a reviewer comment…'
            }
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{ minHeight: 80 }}
          />
          <div className="comment-actions">
            <Button size="sm" type="submit" loading={create.isPending}>
              Comment
            </Button>
          </div>
        </form>
      )}
      {!canComment && !locked && role === 'contributor' && (
        <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
          Contributors reply to reviewer and assessor comments rather than opening new threads.
        </p>
      )}
    </div>
  );
}
